import { usePostsContainer } from 'components/posts/context';
import { useMemo } from 'react';
import { ShiftPostsTarget } from 'app/modules/posts/hooks/useShiftPosts';
import { v4 } from 'uuid';
import { getUniquePrimitiveItemsFromArray } from 'utils/helpers';
import { ListPost } from 'types/PostDetail';
import { PagesProjects } from 'types/Page';

export function useGetInitialTargetsToDuplicate(inputData: {
  initialPosts: ListPost[];
  initialProjects?: PagesProjects;
  postsProjects?: PagesProjects;
}): () => ShiftPostsTarget[] {
  const { initialPosts, initialProjects, postsProjects } = inputData;
  const { activeState, posts } = usePostsContainer();

  return useMemo(
    () => () => {
      const initialPostsIds = initialPosts.map((post) => post.id);
      if (initialPostsIds.length > 0) {
        const postsToDuplicate = posts.allPosts.filter((post) =>
          initialPostsIds.includes(post.id),
        );

        const sourceProfileIds = getUniquePrimitiveItemsFromArray(
          postsToDuplicate.map((post) => post.page.id),
        );

        const targetProfileIds = activeState.profilesIds.filter(
          (id) => !sourceProfileIds.includes(id),
        );

        const targetProfiles = activeState.profiles.filter((page) =>
          targetProfileIds.includes(page.id),
        );

        if (targetProfileIds.length && postsToDuplicate.length) {
          if (initialProjects) {
            const sourceProjectToFindMatch = Object.values(initialProjects)
              .flat()
              .find(
                (project) =>
                  Object.values(postsProjects ? postsProjects : {}).flat()[0]
                    ?.id === project.id,
              );
            const projectNameToFind = sourceProjectToFindMatch?.name
              .trim()
              .toLowerCase();

            const detectedTargets = targetProfiles.map((profile) => {
              const projectWithSameName = initialProjects[profile.id]?.find(
                (project) =>
                  project.name.trim().toLowerCase() === projectNameToFind,
              );
              return {
                id: v4(),
                profileId: profile.id,
                projectId: projectWithSameName?.id,
              };
            });

            if (detectedTargets.length) {
              return detectedTargets;
            }
          }
        }
      }

      return [
        {
          id: v4(),
          profileId: undefined,
          projectId: undefined,
        },
      ];
    },
    [
      initialPosts,
      posts.allPosts,
      activeState.profilesIds,
      activeState.profiles,
      initialProjects,
      postsProjects,
    ],
  );
}
